import React, { useMemo } from 'react'

import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Heading from '@react/react-spectrum/Heading'
import Help from '@react/react-spectrum/Icon/Help'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Radio from '@react/react-spectrum/Radio'
import RadioGroup from '@react/react-spectrum/RadioGroup'
import Select from '@react/react-spectrum/Select'
import Switch from '@react/react-spectrum/Switch'
import Textfield from '@react/react-spectrum/Textfield'
import Tooltip from '@react/react-spectrum/Tooltip'

import LocalePicker from 'components/LocalePicker'
import TranslateDialog from 'components/TranslateDialog/TranslateDialog'

import { WEB as config } from 'config/projects/web'
import { useScreens } from 'hooks'

export function WebSettings({ settings, disabled, onChange }) {
  const { screens } = useScreens()

  const defaultCartStorage = config.cartStorageOptions?.find(o => o.default)?.value || 'local'

  const checkoutScreens = useMemo(() => {
    return [
      { label: 'Checkout (Basic)', value: 'purchase' },
      { label: 'Checkout (Optionated)', value: 'optionatedPurchase' },
    ].concat(screens?.map(s => ({ label: s.title, value: s.name })))
  }, [screens])

  return (
    <>
      <section>
        <Heading size='4'>General</Heading>

        <FieldLabel label='Locale' labelFor='locale'>
          <LocalePicker value={settings.locale} onChange={onChange('locale')} disabled={disabled} />
          <OverlayTrigger placement='right'>
            <Button className='tooltip-btn' variant='tool' icon={<Help />} />
            <Tooltip>
              Select or type in language/currency locale. Delete field content to see the full list of common options.
            </Tooltip>
          </OverlayTrigger>
        </FieldLabel>

        <FieldLabel label=''>
          <Switch
            checked={!!settings.displayFooter}
            onChange={v => !disabled && onChange('displayFooter')(v)}
            label='Display&nbsp;Footer'
          />
        </FieldLabel>

        <FieldLabel label=''>
          <Switch
            checked={!!settings.displayBlueprints}
            onChange={v => !disabled && onChange('displayBlueprints')(v)}
            label='Display&nbsp;Blueprints'
          />
        </FieldLabel>
      </section>

      <section>
        <Heading size='4'>Translation</Heading>
        <p style={{ margin: '0 0 12px', color: '#6e6e6e', fontSize: 13 }}>
          Translate all visible project content to another language.
        </p>
        {settings.id && <TranslateDialog projectId={settings.id} disabled={disabled} currentLanguage={settings.language} />}
      </section>

      <em>
        Navigation and authentication-related settings
        <br />
        have been moved to Navigation tab.
      </em>

      <section>
        <Heading size='4'>Cart</Heading>
        <Switch checked={settings.useCart} onChange={v => !disabled && onChange('useCart')(v)} label={'enable cart'} />

        {settings.useCart && config.cartStorageOptions?.length > 0 && (
          <FieldLabel label='Cart Storage' labelFor='cartStorage'>
            <Select
              options={config.cartStorageOptions}
              value={settings.cartStorage || defaultCartStorage}
              id='cartStorage'
              onChange={onChange('cartStorage')}
              disabled={disabled}
            />
          </FieldLabel>
        )}
      </section>

      <section>
        <Heading size='4'>Primary Checkout</Heading>
        <FieldLabel label='Button Label' labelFor='primaryCheckoutLabel'>
          <Textfield
            name='primaryCheckoutLabel'
            value={settings.primaryCheckoutLabel || 'Proceed to Checkout'}
            onChange={onChange('primaryCheckoutLabel')}
            disabled={disabled}
          />
        </FieldLabel>
        <FieldLabel label='Button Target' labelFor='primaryCheckoutTarget'>
          <Select
            aria-label='Registration fields'
            options={checkoutScreens}
            value={settings.primaryCheckoutTarget || 'purchase'}
            id='primaryCheckoutTarget'
            onChange={onChange('primaryCheckoutTarget')}
            disabled={disabled}
          />
        </FieldLabel>

        <FieldLabel label='Submit data to:'>
          <RadioGroup
            defaultSelectedValue={settings?.checkoutSubmitType || 'aep'}
            onChange={onChange('checkoutSubmitType')}
          >
            <Radio label='AEP' value='aep' disabled={disabled} />
            <Radio label='Webhook' value='webhook' disabled={disabled} />
          </RadioGroup>
        </FieldLabel>

        {settings.checkoutSubmitType === 'aep' && (
          <FieldLabel label='Event Type'>
            <Textfield
              placeholder='checkout'
              name='checkoutEventType'
              value={settings.checkoutEventType || ''}
              onChange={onChange('checkoutEventType')}
              disabled={disabled}
            />
          </FieldLabel>
        )}
        {settings.checkoutSubmitType === 'webhook' && (
          <FieldLabel label='URL' name='checkoutWebhookUrl'>
            <Textfield
              value={settings.checkoutWebhookUrl || ''}
              onChange={onChange('checkoutWebhookUrl')}
              disabled={disabled}
            />
          </FieldLabel>
        )}

        <Heading size='4'>Alternative Checkout</Heading>
        <FieldLabel label='Button label' labelFor='secondaryCheckoutLabel'>
          <Textfield
            name='secondaryCheckoutLabel'
            value={settings.secondaryCheckoutLabel || 'Continue Shopping'}
            onChange={onChange('secondaryCheckoutLabel')}
            disabled={disabled}
          />
        </FieldLabel>
        <FieldLabel label='Button Target' labelFor='secondaryCheckoutTarget'>
          <Select
            aria-label='Registration fields'
            options={checkoutScreens}
            value={settings.secondaryCheckoutTarget || 'home'}
            id='secondaryCheckoutTarget'
            onChange={onChange('secondaryCheckoutTarget')}
            disabled={disabled}
          />
        </FieldLabel>
      </section>
    </>
  )
}
